import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Progress } from "@/components/ui/progress";
import {
  CLIENT_AI_WORKOUT_STEP_LABELS,
  CLIENT_AI_WORKOUT_STEP_PROGRESS,
  cancelClientAiWorkoutGeneration,
  confirmClientAiWorkoutGeneration,
  startClientAiWorkoutGeneration,
  waitForClientAiWorkoutJob,
  type ClientAiWorkoutDraft,
  type ClientAiWorkoutDraftGrouping,
  type ClientAiWorkoutJobStep,
} from "@/integrations/backend/client-ai-workout";
import { formatLocalDateToYYYYMMDD } from "@/lib/date-only";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type DialogPhase = "prompt" | "generating" | "review";

function groupingLabel(grouping: ClientAiWorkoutDraftGrouping): string | null {
  const count = grouping.exercises?.length || 0;
  if (count >= 3) return "Circuit";
  if (count === 2) return "Superset";
  return null;
}

function formatExerciseMeta(exercise: {
  sets?: number | null;
  reps?: number | null;
  time_seconds?: number | null;
  groupingSets?: number | null;
}): string {
  const sets = exercise.sets ?? exercise.groupingSets ?? null;
  const parts: string[] = [];
  if (sets != null && sets > 0) {
    parts.push(`${sets} ${sets === 1 ? "set" : "sets"}`);
  }
  if (exercise.reps != null && exercise.reps > 0) {
    parts.push(`${exercise.reps} reps`);
  } else if (exercise.time_seconds != null && exercise.time_seconds > 0) {
    parts.push(`${exercise.time_seconds}s`);
  }
  return parts.join(" · ");
}

function DraftReviewSummary({ draft }: { draft: ClientAiWorkoutDraft }) {
  const groupings = draft.groupings || [];

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <h3 className="text-lg font-semibold text-white leading-snug">{draft.name || "Your workout"}</h3>
        {draft.duration_minutes != null && Number(draft.duration_minutes) > 0 && (
          <p className="text-sm text-gray-light">{draft.duration_minutes} min</p>
        )}
        {draft.description ? (
          <p className="text-sm text-gray-light pt-1">{draft.description}</p>
        ) : null}
      </div>

      <div className="space-y-3">
        <h4 className="text-sm font-medium text-white">Exercises</h4>
        {groupings.length === 0 ? (
          <p className="text-sm text-gray-light">No exercises in this draft.</p>
        ) : (
          <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1">
            {groupings.map((grouping, gIdx) => {
              const label = groupingLabel(grouping);
              const exercises = grouping.exercises || [];
              if (!label || exercises.length <= 1) {
                const exercise = exercises[0];
                if (!exercise) return null;
                const meta = formatExerciseMeta({
                  sets: exercise.sets,
                  reps: exercise.reps,
                  time_seconds: exercise.time_seconds,
                  groupingSets: grouping.sets,
                });
                return (
                  <div
                    key={`g-${gIdx}`}
                    className="flex items-center justify-between gap-3 rounded-lg border border-primary/20 bg-dark-slate px-3 py-2"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium leading-snug truncate sm:whitespace-normal sm:break-words">
                        {exercise.exercise_name}
                      </div>
                    </div>
                    {meta ? <div className="shrink-0 text-xs text-gray-light">{meta}</div> : null}
                  </div>
                );
              }

              return (
                <div
                  key={`g-${gIdx}`}
                  className="overflow-hidden rounded-lg border border-primary/20 bg-dark-slate"
                >
                  <div className="flex items-center justify-between gap-3 border-b border-primary/20 bg-primary/10 px-3 py-2">
                    <span className="text-sm font-semibold text-primary">{label}</span>
                    {grouping.sets != null && grouping.sets > 0 ? (
                      <span className="text-xs text-gray-light">
                        {grouping.sets} {grouping.sets === 1 ? "round" : "rounds"}
                      </span>
                    ) : null}
                  </div>
                  <div className="space-y-2 p-3">
                    {exercises.map((exercise, index) => {
                      const meta = formatExerciseMeta({
                        sets: exercise.sets,
                        reps: exercise.reps,
                        time_seconds: exercise.time_seconds,
                        groupingSets: grouping.sets,
                      });
                      return (
                        <div key={`g-${gIdx}-e-${index}`} className="space-y-2">
                          {index > 0 && (
                            <div className="text-center text-xs font-medium uppercase tracking-wide text-primary/80">
                              Then
                            </div>
                          )}
                          <div className="flex items-center justify-between gap-3 rounded-lg border border-primary/15 bg-black/10 px-3 py-2">
                            <div className="min-w-0 flex-1">
                              <div className="text-sm font-medium leading-snug truncate sm:whitespace-normal sm:break-words">
                                {exercise.exercise_name}
                              </div>
                            </div>
                            {meta ? (
                              <div className="shrink-0 text-xs text-gray-light">{meta}</div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export function AiWorkoutGeneratorDialog({ open, onOpenChange }: Props) {
  const navigate = useNavigate();
  const [userPrompt, setUserPrompt] = useState("");
  const [phase, setPhase] = useState<DialogPhase>("prompt");
  const [step, setStep] = useState<ClientAiWorkoutJobStep>("gathering");
  const [draft, setDraft] = useState<ClientAiWorkoutDraft | null>(null);
  const [reviewJobId, setReviewJobId] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const cancelledRef = useRef(false);
  const activeJobIdRef = useRef<string | null>(null);

  const requestBackendCancel = (jobId: string | null) => {
    if (!jobId) return;
    void cancelClientAiWorkoutGeneration(jobId);
  };

  const resetToPrompt = () => {
    setPhase("prompt");
    setStep("gathering");
    setDraft(null);
    setReviewJobId(null);
    setConfirming(false);
  };

  useEffect(() => {
    if (open) {
      cancelledRef.current = false;
    } else {
      cancelledRef.current = true;
      const jobId = activeJobIdRef.current;
      activeJobIdRef.current = null;
      // Only cancel in-flight generation; leave awaiting_review drafts if user closed mid-review
      // via explicit discard. Closing the dialog while reviewing discards the draft.
      if (jobId) {
        requestBackendCancel(jobId);
      }
      resetToPrompt();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      const jobId = activeJobIdRef.current;
      activeJobIdRef.current = null;
      requestBackendCancel(jobId);
    };
  }, []);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      cancelledRef.current = true;
      const jobId = activeJobIdRef.current || reviewJobId;
      activeJobIdRef.current = null;
      requestBackendCancel(jobId);
      resetToPrompt();
      onOpenChange(false);
      return;
    }
    onOpenChange(next);
  };

  const handleGenerate = async () => {
    cancelledRef.current = false;
    setPhase("generating");
    setStep("gathering");
    setDraft(null);
    setReviewJobId(null);
    try {
      const started = await startClientAiWorkoutGeneration({
        prompt: userPrompt.trim(),
        date: formatLocalDateToYYYYMMDD(new Date()),
      });
      activeJobIdRef.current = started.job_id;
      if (cancelledRef.current) {
        requestBackendCancel(started.job_id);
        activeJobIdRef.current = null;
        return;
      }

      const job = await waitForClientAiWorkoutJob(
        started.job_id,
        (update) => {
          if (!cancelledRef.current) setStep(update.step);
        },
        () => cancelledRef.current
      );
      if (cancelledRef.current || job.status === "cancelled") {
        activeJobIdRef.current = null;
        resetToPrompt();
        return;
      }

      if (job.status === "failed") {
        toast.error(job.error || "Couldn't generate a workout. Please try again.");
        activeJobIdRef.current = null;
        resetToPrompt();
        return;
      }

      if (job.status === "awaiting_review") {
        if (!job.draft) {
          toast.error("Workout was generated but couldn't be previewed. Please try again.");
          requestBackendCancel(job.job_id);
          activeJobIdRef.current = null;
          resetToPrompt();
          return;
        }
        activeJobIdRef.current = job.job_id;
        setReviewJobId(job.job_id);
        setDraft(job.draft);
        setPhase("review");
        return;
      }

      // Legacy path: already persisted (should not happen with the new pipeline).
      const assignedId = job.assigned_workout_id;
      if (!assignedId) {
        toast.error("Workout was created but couldn't be opened. Check your schedule.");
        activeJobIdRef.current = null;
        resetToPrompt();
        return;
      }

      activeJobIdRef.current = null;
      onOpenChange(false);
      setUserPrompt("");
      resetToPrompt();
      navigate(`/start-workout?assigned=${encodeURIComponent(assignedId)}`);
    } catch (error) {
      if (cancelledRef.current) return;
      if (error instanceof DOMException && error.name === "AbortError") return;
      toast.error(error instanceof Error ? error.message : "Couldn't generate a workout. Please try again.");
      activeJobIdRef.current = null;
      resetToPrompt();
    }
  };

  const handleDiscard = () => {
    const jobId = reviewJobId || activeJobIdRef.current;
    activeJobIdRef.current = null;
    requestBackendCancel(jobId);
    setUserPrompt("");
    resetToPrompt();
    onOpenChange(false);
  };

  const handleConfirm = async () => {
    const jobId = reviewJobId;
    if (!jobId) return;
    setConfirming(true);
    try {
      const confirmed = await confirmClientAiWorkoutGeneration(jobId);
      const assignedId = confirmed.assigned_workout_id;
      if (!assignedId) {
        toast.error("Workout was saved but couldn't be opened. Check your schedule.");
        return;
      }
      activeJobIdRef.current = null;
      setReviewJobId(null);
      onOpenChange(false);
      setUserPrompt("");
      resetToPrompt();
      navigate(`/start-workout?assigned=${encodeURIComponent(assignedId)}`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Couldn't save your workout. Please try again.");
    } finally {
      setConfirming(false);
    }
  };

  const progress = phase === "generating" ? CLIENT_AI_WORKOUT_STEP_PROGRESS[step] : 0;
  const stepLabel = CLIENT_AI_WORKOUT_STEP_LABELS[step];
  const dialogMaxWidth = useMemo(
    () => (phase === "review" ? "max-w-lg" : "max-w-md"),
    [phase]
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`${dialogMaxWidth} rounded-2xl`}>
        <DialogHeader>
          <DialogTitle className="text-xl">
            <div className="flex items-center">
              <Zap className="mr-2 text-primary" />
              {phase === "review" ? "Review your workout" : "AI Workout Generator"}
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {phase === "generating" ? (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-3">
                <Loader2 className="h-5 w-5 animate-spin text-primary shrink-0" />
                <p className="text-sm text-white">{stepLabel}</p>
              </div>
              <Progress value={progress} className="h-2" />
              <p className="text-xs text-gray-light">
                This usually takes a minute or two. Hang tight.
              </p>
            </div>
          ) : phase === "review" && draft ? (
            <DraftReviewSummary draft={draft} />
          ) : (
            <>
              <p className="text-gray-light">
                Tell me what you need — hotel room, new injury, short session, or leave it blank and
                I&apos;ll build something that fits your training.
              </p>
              <div className="border rounded-xl overflow-hidden">
                <div className="bg-dark-slate px-3 py-2 border-b text-sm font-medium">
                  Your request
                </div>
                <textarea
                  className="w-full p-3 text-sm outline-none min-h-[100px] bg-dark-card text-white"
                  placeholder={"e.g., \"I'm in a hotel — give me a room workout\" or \"I have a new shoulder injury\""}
                  value={userPrompt}
                  onChange={(e) => setUserPrompt(e.target.value)}
                  disabled={phase === "generating" || confirming}
                />
              </div>
            </>
          )}
          <div className="flex justify-end space-x-2">
            {phase === "review" ? (
              <>
                <Button variant="outline" onClick={handleDiscard} disabled={confirming}>
                  Not this
                </Button>
                <Button
                  className="gradient-primary text-black"
                  onClick={() => void handleConfirm()}
                  disabled={confirming}
                >
                  {confirming ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin text-black" />
                      Starting…
                    </>
                  ) : (
                    <>
                      Start workout <Zap className="ml-1 h-4 w-4 text-black" />
                    </>
                  )}
                </Button>
              </>
            ) : (
              <>
                <Button variant="outline" onClick={() => handleOpenChange(false)}>
                  {phase === "generating" ? "Stop" : "Cancel"}
                </Button>
                <Button
                  className="gradient-primary text-black"
                  onClick={() => void handleGenerate()}
                  disabled={phase === "generating"}
                >
                  {phase === "generating" ? (
                    <>
                      <Loader2 className="mr-1 h-4 w-4 animate-spin text-black" />
                      Generating…
                    </>
                  ) : (
                    <>
                      Generate Workout <Zap className="ml-1 h-4 w-4 text-black" />
                    </>
                  )}
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
